"use client";

import React, { useState, useEffect } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="17663" className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="text-foreground/90 hover:text-primary font-sans font-medium transition-colors tracking-tight">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-4 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/50 pb-2">
          <SheetClose asChild>
            <Link to={item.href || "#"} className="block text-lg font-medium text-foreground hover:text-primary transition-colors font-sans">
              {item.label}
            </Link>
          </SheetClose>
        </div>)}
      <div className="mt-6">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full shadow-md" asChild>
          <Link to="tel:626-268-5990">
            <Phone className="mr-2 h-4 w-4" />
            Call Now
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md shadow-md border-border/40" : "bg-background")}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {}
          <Link to="#" className="flex-shrink-0 flex items-center gap-3">
            
            <h1 className="font-serif text-lg md:text-xl font-bold text-foreground leading-tight tracking-tight">
              Smart Choice Dentures & Implants
            </h1>
          </Link>

          {}
          <div className="hidden lg:flex items-center gap-8">
            <MenuProvider menu_id="43">
              <DesktopMenuConsumer />
            </MenuProvider>

            {}
            <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full shadow-lg relative overflow-hidden group transition-transform hover:scale-105">
              <Link to="tel:626-268-5990" className="flex items-center gap-2 pl-5 pr-6">
                <span className="relative flex h-3 w-3 mr-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                </span>
                Free Consultation
              </Link>
            </Button>
          </div>

          {}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-secondary">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1617360/img/precision_flow_logo.png" alt="Smart Choice Dentures & Implants" className="h-8 w-auto object-contain" />
                  </SheetTitle>
                </SheetHeader>
                <MenuProvider menu_id="43">
                  <MobileMenuConsumer />
                </MenuProvider>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>;
}